import React, { useEffect, useMemo, useState } from 'react';
import type { AqItem, AqItemType, AqPriority, AqTone } from '../../growth-approvals/types';
import { AQ_ITEMS } from '../../growth-approvals/mockData';
import { AQ_TABS, AQ_TYPE_META, GROWTH_APPROVALS_META } from '../../growth-approvals/config';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import './growth-approvals.css';

const TONE: Record<AqTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  iris: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const PRIORITY_TONE: Record<AqPriority, AqTone> = {
  high: 'terra',
  medium: 'gold',
  low: 'neutral',
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function toTimeLabel(minutesAgo: number): string {
  if (minutesAgo < 60) return `${String(minutesAgo)} min ago`;
  return `${String(Math.round(minutesAgo / 60))}h ago`;
}

function AqCard({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

function PriorityDot({ priority }: { priority: AqPriority }) {
  return (
    <span
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ background: TONE[PRIORITY_TONE[priority]].accent }}
    />
  );
}

function TypeBadge({ type }: { type: AqItemType }) {
  const meta = AQ_TYPE_META[type];
  const t = TONE[meta.tone];
  return (
    <span
      className="inline-flex h-7 w-7 items-center justify-center rounded-[10px]"
      style={{ background: t.tint, color: t.accent }}
    >
      <Icon kind={meta.icon as IconKind} size={15} stroke={1.9} />
    </span>
  );
}

function AqQueueCard({
  item,
  active,
  onClick,
}: {
  item: AqItem;
  active: boolean;
  onClick: () => void;
}) {
  const meta = AQ_TYPE_META[item.type];
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full cursor-pointer border-b border-[var(--cream-2)] px-4 py-4 text-left transition last:border-b-0',
        active
          ? 'bg-[var(--success-tint)]'
          : 'bg-[var(--cream-1)] hover:bg-[var(--surface-mute)]',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TypeBadge type={item.type} />
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.04em]"
            style={{ color: TONE[meta.tone].accent }}
          >
            {meta.label}
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
          <PriorityDot priority={item.priority} />
          <span className="font-[var(--font-mono)]">{toTimeLabel(item.minutesAgo)}</span>
        </span>
      </div>

      <p className="mt-3 text-[15px] font-semibold text-[var(--ink-0)]">{item.title}</p>
      <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[var(--ink-3)]">{item.summary}</p>
    </button>
  );
}

function AqDetailPanel({
  item,
  onApprove,
  onReject,
  onReview,
}: {
  item: AqItem;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onReview?: (item: AqItem) => void;
}) {
  const meta = AQ_TYPE_META[item.type];
  const priorityTone = TONE[PRIORITY_TONE[item.priority]];

  return (
    <AqCard className="flex flex-col overflow-hidden">
      <div className="px-5 py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-[12px]"
              style={{ background: TONE[meta.tone].tint, color: TONE[meta.tone].accent }}
            >
              <Icon kind={meta.icon as IconKind} size={18} stroke={1.9} />
            </span>
            <div>
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.05em]"
                style={{ color: TONE[meta.tone].accent }}
              >
                {meta.label}
              </p>
              <p className="mt-1 font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
                {item.createdBy} · {toTimeLabel(item.minutesAgo)}
              </p>
            </div>
          </div>

          <span
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.05em]"
            style={{ color: priorityTone.accent }}
          >
            <PriorityDot priority={item.priority} />
            {item.priority} priority
          </span>
        </div>

        <h2 className="mt-4 font-[var(--font-display)] text-[22px] font-semibold leading-[1.2] tracking-[-0.025em] text-[var(--ink-0)]">
          {item.title}
        </h2>
        <p className="mt-4 text-[14px] leading-[1.65] text-[var(--ink-2)]">
          {item.summary} You will see full details and projected impact on the review page before
          final approval.
        </p>

        <div className="my-5 border-t border-[var(--cream-2)]" />

        <div>
          <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--success)]">
            <Icon kind="sparkle" size={14} stroke={1.9} />
            Why ELVA decided this
          </p>
          <div className="mt-3 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[var(--success-tint)] px-4 py-3">
            <p className="text-[13.5px] leading-[1.6] text-[var(--ink-2)]">{item.reason}</p>
          </div>
        </div>

        <div className="mt-5">
          <p className={eyebrowClass}>Quick facts</p>
          <div className="mt-3 overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)]">
            {item.facts.map((fact, index) => (
              <div
                key={fact.label}
                className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-[var(--cream-2)] px-4 py-3 last:border-b-0"
                style={{ background: index % 2 === 0 ? 'var(--cream-1)' : 'var(--surface-mute)' }}
              >
                <p className="text-[13px] font-medium text-[var(--ink-3)]">{fact.label}</p>
                <p className="text-right text-[13.5px] font-semibold text-[var(--ink-1)]">
                  {fact.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2.5 rounded-[var(--r-md)] bg-[var(--surface-mute)] px-4 py-3 text-[12.5px] leading-[1.55] text-[var(--ink-3)]">
          <Icon kind="info" size={15} stroke={1.9} className="mt-0.5 shrink-0" />
          <span>
            This is a preview. Click{' '}
            <span className="font-semibold text-[var(--ink-1)]">
              Review {meta.action}
            </span>{' '}
            to see full details and take action (approve, edit, or regenerate).
          </span>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-[var(--cream-2)] px-5 py-4">
        <button
          type="button"
          className="inline-flex h-10 cursor-pointer items-center rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 text-[13px] font-medium text-[var(--ink-3)] transition hover:bg-[var(--surface-mute)]"
          onClick={() => onReject?.(item.id)}
        >
          Quick Reject
        </button>

        <button
          type="button"
          className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-6 text-[14px] font-semibold text-white"
          style={{ background: 'var(--success)' }}
          onClick={() => (onReview ? onReview(item) : onApprove?.(item.id))}
        >
          Review {meta.action}
          <Icon kind="right" size={15} stroke={2.4} />
        </button>
      </div>
    </AqCard>
  );
}

export interface ApprovalQueueViewProps {
  items?: AqItem[];
  tabCounts?: Record<string, number>;
  activeTab?: AqItemType | 'all';
  onTabChange?: (tab: AqItemType | 'all') => void;
  loading?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onReview?: (item: AqItem) => void;
}

export function ApprovalQueueView({
  items: itemsProp,
  tabCounts,
  activeTab: activeTabProp,
  onTabChange,
  loading = false,
  onApprove,
  onReject,
  onReview,
}: ApprovalQueueViewProps = {}) {
  const meta = GROWTH_APPROVALS_META;
  const controlled = itemsProp !== undefined;
  const sourceItems = controlled ? itemsProp : AQ_ITEMS;
  const [internalTab, setInternalTab] = useState<AqItemType | 'all'>('all');
  const activeTab = activeTabProp ?? internalTab;
  const setActiveTab = (tab: AqItemType | 'all') => {
    onTabChange?.(tab);
    if (activeTabProp === undefined) setInternalTab(tab);
  };
  const [selectedId, setSelectedId] = useState<string>(sourceItems[0]?.id ?? '');

  const counts = useMemo(() => {
    if (tabCounts) return tabCounts;
    const next: Record<string, number> = { all: sourceItems.length };
    for (const tab of AQ_TABS) {
      if (tab.id === 'all') continue;
      next[tab.id] = sourceItems.filter((item) => item.type === tab.id).length;
    }
    return next;
  }, [sourceItems, tabCounts]);

  const filteredItems = useMemo(
    () => (activeTab === 'all' ? sourceItems : sourceItems.filter((item) => item.type === activeTab)),
    [activeTab, sourceItems],
  );

  const selectedItem = useMemo(() => {
    const matched = filteredItems.find((item) => item.id === selectedId);
    return matched ?? filteredItems[0] ?? sourceItems[0];
  }, [filteredItems, selectedId, sourceItems]);

  useEffect(() => {
    if (filteredItems.length === 0) return;
    if (!filteredItems.some((item) => item.id === selectedId)) {
      setSelectedId(filteredItems[0]!.id);
    }
  }, [filteredItems, selectedId]);

  const waitingTotal = tabCounts?.all ?? sourceItems.length;

  return (
    <div className="growth-approvals-root relative mx-auto w-full max-w-[1180px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="check-circ" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
              {meta.description}
            </p>
          </div>

          <span
            className="inline-flex h-12 shrink-0 items-center gap-2 rounded-[var(--r-md)] px-5 text-white"
            style={{ background: 'var(--grad-ink)' }}
          >
            <span className="font-[var(--font-display)] text-[22px] font-bold leading-none">
              {waitingTotal}
            </span>
            <span className="text-[13px] text-white/85">
              items waiting
              {loading ? ' · Updating…' : ''}
            </span>
          </span>
        </div>
      </div>

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-[var(--cream-2)]">
        {AQ_TABS.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={[
                '-mb-px inline-flex cursor-pointer items-center gap-2 whitespace-nowrap border-0 border-b-2 bg-transparent px-4 py-2.5 text-[13px] font-semibold transition',
                active
                  ? 'border-[var(--ai-3)] text-[var(--ink-0)]'
                  : 'border-transparent text-[var(--ink-3)]',
              ].join(' ')}
            >
              {tab.label}
              <span
                className="inline-flex min-w-[20px] items-center justify-center rounded-[var(--r-pill)] px-1.5 py-0.5 text-[11px] font-bold"
                style={{
                  background: active
                    ? 'color-mix(in srgb, var(--ai-3) 16%, transparent)'
                    : 'var(--surface-mute)',
                  color: active ? 'var(--ai-3)' : 'var(--ink-3)',
                }}
              >
                {counts[tab.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="aq-split grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,46%)_minmax(0,54%)]">
        <AqCard className="overflow-hidden">
          <div className="aq-scroll max-h-[690px] overflow-y-auto">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <AqQueueCard
                  key={item.id}
                  item={item}
                  active={selectedItem?.id === item.id}
                  onClick={() => {
                    setSelectedId(item.id);
                  }}
                />
              ))
            ) : (
              <div className="px-6 py-16 text-center">
                <Icon
                  kind="check-circ"
                  size={40}
                  stroke={1.6}
                  className="mx-auto text-[var(--success)]"
                />
                <p className="mt-3 font-[var(--font-display)] text-[16px] font-semibold text-[var(--ink-1)]">
                  All caught up!
                </p>
                <p className="mt-1.5 text-[13px] text-[var(--ink-3)]">
                  No items in this category need your approval.
                </p>
              </div>
            )}
          </div>
        </AqCard>

        {selectedItem ? (
          <AqDetailPanel
            item={selectedItem}
            {...(onApprove ? { onApprove } : {})}
            {...(onReject ? { onReject } : {})}
            {...(onReview ? { onReview } : {})}
          />
        ) : null}
      </div>
    </div>
  );
}
